// src/components/ReservationForm/ReservationForm.jsx
import React, { useState, useEffect } from 'react';
import styles from './ReservationForm.module.css';
import FormInput from '../FormInput/FormInput';
import Button from '../Button/Button';
// Import both create and cancel service functions
import { createReservation, cancelReservation } from '../../services/reservationService';
import { listAllAccounts } from '../../services/accountService';

const ReservationForm = ({
    selectedDates = [],     // For selecting new dates
    onReservationSuccess,   // Callback after successful NEW booking
    viewingReservation,     // Object: contains details of reservation clicked on calendar, or null
    onClearView,            // Callback to tell parent to exit view mode
    onCancelSuccess,        // Callback after successful CANCELLATION
    currentUser,            // Pass logged-in user info
    formTitle,              // Prop for the form title
    formContext = 'my-account', // 'my-account' or 'admin'
    expiryHours = 48        // Hours before a pending reservation expires (driven by admin setting)
}) => {

    // --- State ---
    const [description, setDescription] = useState(''); // Will be used for 'eventDescription'
    const [isLoading, setIsLoading] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [submitMessage, setSubmitMessage] = useState('');
    const [isViewing, setIsViewing] = useState(false);

    const [eventName, setEventName] = useState('');
    const [additionalInfo, setAdditionalInfo] = useState('');

    // --- Unification: New Guest Fields State ---
    const [guestName, setGuestName] = useState('');
    const [guestEmail, setGuestEmail] = useState('');
    const [guestPhone, setGuestPhone] = useState('');
    const [groupDescription, setGroupDescription] = useState('');
    const [purposeOfVisit, setPurposeOfVisit] = useState('');
    const [adultCount, setAdultCount] = useState(0);
    const [childCount, setChildCount] = useState(0);
    const [responsiblePerson, setResponsiblePerson] = useState('');
    const [accountId, setAccountId] = useState('');
    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);

    useEffect(() => {
        const fetchAccounts = async () => {
            if (formContext === 'admin') {
                setAccountsLoading(true);
                try {
                    const data = await listAllAccounts();
                    setAccounts(data);
                } catch (err) {
                    console.error("Failed to load accounts for admin form", err);
                } finally {
                    setAccountsLoading(false);
                }
            }
        };
        fetchAccounts();
    }, [formContext]);

    // --- Effect to Populate Form when Viewing/Clearing ---
    useEffect(() => {
        setSubmitStatus(null);
        setSubmitMessage('');

        if (viewingReservation) {
            setIsViewing(true);
            setEventName(viewingReservation.eventName || '');
            setDescription(viewingReservation.eventDescription || viewingReservation.notes || '');
            setAdditionalInfo(viewingReservation.additionalInfo || '');

            setGuestName(viewingReservation.guestName || '');
            setGuestEmail(viewingReservation.guestEmail || '');
            setGuestPhone(viewingReservation.guestPhone || '');
            setGroupDescription(viewingReservation.groupDescription || '');
            setPurposeOfVisit(viewingReservation.purposeOfVisit || '');
            setAdultCount(viewingReservation.adultCount || 0);
            setChildCount(viewingReservation.childCount || 0);
            setResponsiblePerson(viewingReservation.responsiblePerson || '');
            setAccountId(viewingReservation.accountId || '');

        } else {
            setIsViewing(false);
            setEventName('');
            setDescription('');
            setAdditionalInfo('');

            // PER USER REQUEST: Don't auto-fill name/email anymore
            setGuestName('');
            setGuestEmail('');
            setGuestPhone('');
            setGroupDescription('');
            setPurposeOfVisit('');
            setAdultCount(0);
            setChildCount(0);
            setResponsiblePerson('');

            // Handle Church (AccountId) based on context
            if (formContext === 'my-account' && currentUser) {
                // If member, pre-set their account and lock it
                setAccountId(currentUser.accountId || '');
            } else {
                setAccountId('');
            }
        }
    }, [viewingReservation, currentUser, formContext]);
    // --- End Effect ---

    // --- Date Formatting Helper Function ---
    const formatDateForDisplay = (dateStr) => {
        if (!dateStr) return '';
        const parts = dateStr.split('-');
        if (parts.length !== 3) return dateStr; // Fallback for unexpected format

        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
        const day = parseInt(parts[2], 10);

        const dateObj = new Date(Date.UTC(year, month, day)); // Use UTC for consistency
        return dateObj.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long', // Full month name e.g., "July"
            day: 'numeric',
            timeZone: 'UTC' // Ensure output reflects UTC date
        });
    };
    // --- End Date Formatting Helper ---

    // --- Handler for Submitting a NEW Reservation Request ---
    const handleReserveSubmit = async (e) => {
        e.preventDefault();
        if (isViewing) return;

        setSubmitStatus(null);
        setSubmitMessage('');

        if (selectedDates.length === 0) {
            setSubmitStatus('error');
            setSubmitMessage('Please select at least one date from the calendar.');
            return;
        }

        // Auto-generate Event Name if needed (similar to public form)
        let finalEventName = eventName.trim();
        if (!finalEventName) {
            finalEventName = groupDescription ? `${groupDescription} Reservation` : `Reservation for ${guestName}`;
        }

        // We can use Purpose of Visit as Event Description if that's empty
        let finalDescription = description.trim();
        if (!finalDescription) {
            finalDescription = purposeOfVisit || 'No description provided';
        }

        const sortedDates = [...selectedDates].sort();
        const startDateStr = sortedDates[0];
        const endDateStr = sortedDates[sortedDates.length - 1];

        // Ensure parseDateString handles timezone correctly for reliable diff calculation
        const parseDateStringForDiff = (dateStr) => {
            const [year, month, day] = dateStr.split('-').map(Number);
            return new Date(Date.UTC(year, month - 1, day)); // Use UTC
        };

        const startDateForDiff = parseDateStringForDiff(startDateStr);
        const endDateForDiff = parseDateStringForDiff(endDateStr);

        const oneDayInMs = 1000 * 60 * 60 * 24;
        // Add a small epsilon for floating point comparisons if necessary, or ensure integer arithmetic
        const diffInTime = endDateForDiff.getTime() - startDateForDiff.getTime();
        const expectedDays = Math.round(diffInTime / oneDayInMs) + 1;


        if (selectedDates.length !== expectedDays) {
            setSubmitStatus('error');
            setSubmitMessage('Please select a single contiguous block of dates (no gaps).');
            return;
        }

        const startTime = new Date(`${startDateStr}T00:00:00.000Z`); // ISO format implies UTC
        const endTime = new Date(`${endDateStr}T23:59:59.999Z`);   // ISO format implies UTC

        if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
            setSubmitStatus('error');
            setSubmitMessage('Invalid date range selected.');
            return;
        }

        const reservationData = {
            startTime: startTime.toISOString(),
            endTime: endTime.toISOString(),
            startTime: startTime.toISOString(),
            endTime: endTime.toISOString(),
            eventName: finalEventName,
            eventDescription: finalDescription,
            additionalInfo: additionalInfo.trim(),
            // Unified Fields
            guestName: guestName.trim(),
            guestEmail: guestEmail.trim(),
            guestPhone: guestPhone.trim(),
            groupDescription: groupDescription.trim(),
            purposeOfVisit: purposeOfVisit.trim(),
            adultCount: parseInt(adultCount) || 0,
            childCount: parseInt(childCount) || 0,
            responsiblePerson: responsiblePerson.trim(),
            accountId: accountId ? parseInt(accountId) : undefined,
        };

        setIsLoading(true);
        try {
            const result = await createReservation(reservationData);
            setSubmitStatus('success');
            setSubmitMessage(result.message || 'Reservation request submitted successfully! Pending approval.');

            setEventName('');
            setDescription('');
            setAdditionalInfo('');
            // Reset unified fields (or re-apply defaults via effect, but manual reset is safer before unnmount/remount)
            setGroupDescription('');
            setPurposeOfVisit('');
            setAdultCount(0);
            setChildCount(0);
            setResponsiblePerson('');
            // Optional: reset guestName/Email to user defaults? Effect will handle this when 'selectedDates' changes usually.

            if (typeof onReservationSuccess === 'function') {
                onReservationSuccess();
            }
        } catch (error) {
            setSubmitStatus('error');
            setSubmitMessage(error.response?.data?.message || error.message || 'Failed to submit reservation. A day in the selected range might be unavailable or input is invalid.');
        } finally {
            setIsLoading(false);
        }
    };
    // --- End NEW Reservation Submit ---


    // --- Handler for Clicking the CANCEL Button (when viewing) ---
    const handleCancelClick = async () => {
        if (!isViewing || !viewingReservation) return;

        // Use a consistent formatting for confirm dialog if possible
        const startDateDisplay = viewingReservation.startDate ? formatDateForDisplay(viewingReservation.startDate.split('T')[0]) : formatDateForDisplay(viewingReservation.startTime.split('T')[0]);
        const endDateDisplay = viewingReservation.endDate ? formatDateForDisplay(viewingReservation.endDate.split('T')[0]) : formatDateForDisplay(viewingReservation.endTime.split('T')[0]);

        if (!window.confirm(`Are you sure you want to cancel your reservation for ${viewingReservation.eventName || 'this event'} from ${startDateDisplay} to ${endDateDisplay}?`)) {
            return;
        }

        setIsLoading(true);
        setSubmitStatus(null);
        setSubmitMessage('');
        try {
            const result = await cancelReservation(viewingReservation.id);
            setSubmitStatus('success');
            setSubmitMessage(result.message || 'Reservation cancelled successfully.');
            if (typeof onCancelSuccess === 'function') {
                onCancelSuccess();
            } else if (typeof onClearView === 'function') {
                onClearView(); // Fallback to clear view if no specific cancel success handler
            }
        } catch (error) {
            setSubmitStatus('error');
            setSubmitMessage(error.response?.data?.message || error.message || 'Failed to cancel reservation.');
        } finally {
            setIsLoading(false);
        }
    };
    // --- End CANCEL Button Handler ---


    // --- Handler for "New Reservation / Clear View" button ---
    const handleClearClick = () => {
        if (typeof onClearView === 'function') {
            onClearView();
        }
    };
    // --- End CLEAR Button Handler ---


    return (
        <form id="reservation-form" className={`${styles.reservationForm} ${!isViewing ? styles.newReservationFormLayout : ''}`} onSubmit={handleReserveSubmit}>
            {!isViewing && formTitle && (
                <h4 className={styles.formTitleHeader}>{formTitle}</h4>
            )}

            <div className={styles.selectedDatesDisplay}>
                <strong>
                    {isViewing ? 'Viewing Reservation For:' : 'Selected Dates:'}
                </strong>
                {isViewing && viewingReservation ? (
                    <span>
                        {viewingReservation.eventName ? `"${viewingReservation.eventName}" from ` : ""}
                        {formatDateForDisplay(viewingReservation.startTime.split('T')[0])} to {formatDateForDisplay(viewingReservation.endTime.split('T')[0])}
                        (Status: {viewingReservation.status})
                    </span>
                ) : selectedDates.length > 0 ? (
                    (() => {
                        // Ensure dates are sorted to reliably get first and last
                        const sortedDates = [...selectedDates].sort();
                        const firstDateFormatted = formatDateForDisplay(sortedDates[0]);

                        if (sortedDates.length === 1) {
                            return <span> {firstDateFormatted}</span>;
                        } else {
                            const lastDateFormatted = formatDateForDisplay(sortedDates[sortedDates.length - 1]);
                            return <span> {firstDateFormatted} to {lastDateFormatted}</span>;
                        }
                    })() // IIFE to embed logic
                ) : (
                    <em> {!isViewing ? 'Please click dates on the calendar to select.' : 'N/A'}</em>
                )}
            </div>

            {/* "Contact Name/Email" FormInput has been removed */}

            {isViewing && viewingReservation ? (
                <>
                    <FormInput label="Event Name:" id="viewEventName" name="viewEventName" type="text" value={eventName} readOnly />
                    <FormInput label="Event Description:" id="viewEventDescription" name="viewEventDescription" type="textarea" rows={3} value={description} readOnly />
                    {additionalInfo && <FormInput label="Additional Information:" id="viewAdditionalInfo" name="viewAdditionalInfo" type="textarea" rows={3} value={additionalInfo} readOnly />}
                </>
            ) : (
                <>
                    <div className={styles.section} style={{ marginBottom: '1.5rem' }}>
                        <h5 style={{ marginBottom: '0.5rem', marginTop: 0 }}>1. Name of Person Initiating Reservation</h5>
                        <FormInput
                            label="Full Name"
                            type="text"
                            id="guestName"
                            value={guestName}
                            onChange={(e) => setGuestName(e.target.value)}
                            required
                            placeholder="Full Name *"
                        />
                        <div style={{ marginLeft: '1rem', marginTop: '0.5rem' }}>
                            <h6 style={{ margin: '0.5rem 0', color: '#666' }}>a) Contact Information</h6>
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <FormInput
                                    label="Email Address"
                                    type="email"
                                    id="guestEmail"
                                    value={guestEmail}
                                    onChange={(e) => setGuestEmail(e.target.value)}
                                    required
                                    containerStyle={{ flex: 1 }}
                                    placeholder="Email Address *"
                                />
                                <FormInput
                                    label="Cell Phone"
                                    type="tel"
                                    id="guestPhone"
                                    value={guestPhone}
                                    onChange={(e) => setGuestPhone(e.target.value)}
                                    required
                                    containerStyle={{ flex: 1 }}
                                    placeholder="Cell Phone *"
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.section} style={{ marginBottom: '1.5rem' }}>
                        <h5 style={{ marginBottom: '0.5rem', marginTop: 0 }}>2. Name of Church</h5>
                        {formContext === 'admin' ? (
                            <div className={styles.formInputContainer}>
                                <label style={{ display: 'block', marginBottom: '5px' }}>Select Church / Organization *</label>
                                <select
                                    value={accountId}
                                    onChange={(e) => setAccountId(e.target.value)}
                                    required
                                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                                >
                                    <option value="">-- Select Church --</option>
                                    {accounts.map(acc => (
                                        <option key={acc.id} value={acc.id}>{acc.accountName}</option>
                                    ))}
                                </select>
                                {accountsLoading && <span style={{ fontSize: '0.8em', color: '#666' }}>Loading churches...</span>}
                            </div>
                        ) : (
                            <FormInput
                                label="Church / Organization"
                                type="text"
                                value={currentUser?.account?.accountName || 'Your Account'}
                                readOnly
                                disabled
                            />
                        )}
                    </div>

                    <div className={styles.section} style={{ marginBottom: '1.5rem' }}>
                        <h5 style={{ marginBottom: '0.5rem', marginTop: 0 }}>3. Description of Group</h5>
                        <p style={{ fontSize: '0.85em', color: '#666', marginTop: '-0.25rem', marginBottom: '0.5rem' }}>(i.e., College or District retreat; Service Group, etc.)</p>
                        <FormInput
                            label="Group Description"
                            type="text"
                            id="groupDescription"
                            value={groupDescription}
                            onChange={(e) => setGroupDescription(e.target.value)}
                            placeholder="Group Description (i.e., College or District retreat; Service Group, etc.)"
                        />
                        {/* Hidden or Optional Event Name for compatibility if user wants to override */}
                        <div style={{ display: 'none' }}>
                            <FormInput label="Event Name (Optional)" type="text" id="eventName" value={eventName} onChange={(e) => setEventName(e.target.value)} />
                        </div>
                    </div>

                    <div className={styles.section} style={{ marginBottom: '1.5rem' }}>
                        <h5 style={{ marginBottom: '0.5rem', marginTop: 0 }}>4. Purpose of Highview House Visit</h5>
                        <FormInput
                            label="Purpose of Visit"
                            type="textarea"
                            id="purposeOfVisit"
                            rows={3}
                            value={purposeOfVisit}
                            onChange={(e) => setPurposeOfVisit(e.target.value)}
                            placeholder="Briefly describe the purpose of your visit..."
                        />
                    </div>

                    <div className={styles.section} style={{ marginBottom: '1.5rem' }}>
                        <h5 style={{ marginBottom: '0.5rem', marginTop: 0 }}>5. Approximate Number of Attendees</h5>
                        <div style={{ display: 'flex', gap: '1rem' }}>
                            <FormInput
                                label="Adults Expected"
                                type="number"
                                id="adultCount"
                                value={adultCount}
                                onChange={(e) => setAdultCount(e.target.value)}
                                min="0"
                                containerStyle={{ flex: 1 }}
                            />
                            <FormInput
                                label="Children Expected (Under 18)"
                                type="number"
                                id="childCount"
                                value={childCount}
                                onChange={(e) => setChildCount(e.target.value)}
                                min="0"
                                containerStyle={{ flex: 1 }}
                            />
                        </div>
                    </div>

                    <div className={styles.section} style={{ marginBottom: '1.5rem' }}>
                        <h5 style={{ marginBottom: '0.5rem', marginTop: 0 }}>6. Person Responsible for Payment Arrangements</h5>
                        <FormInput
                            label="Name of Responsible Person"
                            type="text"
                            id="responsiblePerson"
                            value={responsiblePerson}
                            onChange={(e) => setResponsiblePerson(e.target.value)}
                            required
                            placeholder="Name of Responsible Person *"
                        />
                        <p style={{ fontSize: '0.85em', color: '#666', marginTop: '0.5rem' }}><strong>Note:</strong> Expires in {expiryHours ?? 48} hour{(expiryHours ?? 48) !== 1 ? 's' : ''} if no payment is made by your selected local church.</p>
                    </div>

                    {/* Keep Generic Additional Info just in case? Or remove to strictly match? Public form doesn't have it. I'll keep it as optional at the bottom or hide it. I'll hide it to reduce clutter if not requested. */}
                    {/* <FormInput label="Additional Info" ... /> */}

                    {/* Old Unified Fields Section Removed (merged above) */}
                </>
            )}

            {submitMessage && (
                <div className={submitStatus === 'success' ? styles.successMessage : styles.errorMessage}>
                    {submitMessage}
                </div>
            )}

            <div className={styles.buttonGroup}>
                {isViewing ? (
                    <>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleClearClick}
                            disabled={isLoading}
                            className={styles.clearButton}
                        >
                            New Reservation / Clear View
                        </Button>
                        {(viewingReservation?.status === 'pending' || viewingReservation?.status === 'approved') && (
                            <Button
                                type="button"
                                variant="outlineDanger"
                                onClick={handleCancelClick}
                                disabled={isLoading}
                                className={styles.cancelButton}
                            >
                                {isLoading ? 'Cancelling...' : 'Cancel This Reservation'}
                            </Button>
                        )}
                    </>
                ) : (
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isLoading || selectedDates.length === 0 || !guestName.trim() || !guestEmail.trim() || !responsiblePerson.trim() || !accountId}
                        className={styles.reserveButton}
                    >
                        {isLoading ? 'Submitting...' : 'Request Reservation'}
                    </Button>
                )}
            </div>
        </form>
    );
};

export default ReservationForm;